<script setup>
import { ref, computed, onMounted } from "vue"
import { useStore } from 'vuex'
import {
    mdiCheck,
    mdiInformationOutline,
    mdiAlertCircleOutline,
    mdiRefresh,
    mdiStar,
    mdiWalletOutline,
} from '@mdi/js'
import Icon from '@/components/Icon.vue'
import JbButton from '@/components/JbButton.vue'
import CardComponent from '@/components/CardComponent.vue'
import { startSubscription, getStripeErrorMessage } from "@/services/stripe.service"
import { getErrorMessage } from "@/utils/error-message"
import { usePromo } from "@/globals/promo"
import { rememberPendingPayment } from "@/store/products.module"

const store = useStore()

/* The running promo on a plan card — Stripe applies the same coupon to a
   subscription checkout as to a one-off one. "For the first month" because
   GET /promo does not say how long the coupon lasts on a subscription: the
   first payment is certain, the renewals are not. Same wording as the plans
   tab on /products. */
const { applyTo: applyPromo } = usePromo()
const planPromoPrice = (plan) => {
    const price = applyPromo(plan.price)
    return price === null ? null : price.toFixed(2)
}

const money = (value) => Number(value).toFixed(2)
const getClientReferenceId = computed(() => (window.Rewardful && window.Rewardful.referral || ('checkout_' + (new Date).getTime())))
// Which plan is mid-redirect. A single shared boolean used to spin every card's
// button at once and left them all clickable, so a double-click could fire two
// checkout redirects.
const pendingPriceId = ref(null)
const loadError = ref("")
// A failed checkout has to say so — this is a payment flow.
const subscribeError = ref("")

const plans = computed(() => {
    const products = store.state.products.deals || []
    return products
        .filter(product => product.plan_id_stripe)
        .map(product => ({
            name: product.name,
            price: product.price,
            productId: product.id,
            priceId: product.plan_id_stripe,
            tokens: product.tokens,
            bonusTokens: product.tokens * 0.1,
            popular: isPopular(product.tokens),
            popularLabel: getPopularLabel(product.tokens)
        }))
})

// `deals` is undefined until the products request settles.
const loading = computed(() => !Array.isArray(store.state.products.deals))

const activeSubscriptions = computed(() =>
    (store.state.subscriptions?.subscriptions || []).filter(
        (sub) => sub.status === 'active' || sub.status === 'trialing'
    )
)

// The overview renders plan_amount as "€{{ amount }}/month", so it lines up
// with the plan price and lets us flag the plan the user is already on.
const isCurrentPlan = (plan) =>
    activeSubscriptions.value.some((sub) => Number(sub.plan_amount) === Number(plan.price))

const isPopular = (tokens) => [53, 320, 1100].includes(tokens)

const getPopularLabel = (tokens) => {
    switch (tokens) {
        case 53: return "Most popular"
        case 320: return "Best value"
        case 1100: return "Largest plan"
        default: return ""
    }
}

const subscribe = async (productId, priceId) => {
    if (pendingPriceId.value !== null) return
    pendingPriceId.value = priceId
    subscribeError.value = ""

    // Stripe's cancel_url is shared with one-off checkout: /cancel needs to know
    // this was a plan, and to bring the customer back to these plans.
    rememberPendingPayment({ kind: 'plan', productId, from: '/subscription-plans' })

    /* Fetch the session over an authenticated request, then navigate to the url
       it returns. Not a link: the endpoint requires an Authorization header, and
       a top-level navigation cannot carry one. The DataFast cookies the old
       `buildUrlWithDataFast` call appended are read inside the service now. */
    try {
        await startSubscription({
            planId: priceId,
            productId,
            reference: getClientReferenceId.value
        })
    } catch (error) {
        // Nothing navigated — the button must not keep spinning.
        pendingPriceId.value = null
        subscribeError.value = getStripeErrorMessage(error, "We couldn't start the subscription. Please try again.")
    }
}

const loadPlans = () => {
    loadError.value = ""
    store.dispatch("products/getTokenProducts").catch((error) => {
        loadError.value = getErrorMessage(error, "We couldn't load the subscription plans.")
    })
}

onMounted(loadPlans)
</script>

<template>
    <div class="space-y-5">
        <!-- Section header -->
        <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Choose your plan</h2>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-300 max-w-2xl">
                A subscription adds 10% more tokens than pay-as-you-go. Cancel anytime.
            </p>
        </div>

        <!-- Info banner -->
        <div class="flex items-start gap-3 rounded-xl bg-gray-50 dark:bg-gray-900 px-4 py-3">
            <icon :path="mdiInformationOutline" :size="18" w="w-5" h="h-5"
                class="text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
            <p class="text-sm text-gray-600 dark:text-gray-300">
                Monthly allocations add to your existing balance. To change plan, cancel the current subscription
                first.
            </p>
        </div>

        <!-- Checkout failure — kept outside the loading/error/plans chain below,
             which is a single v-if/v-else-if sequence. -->
        <div v-if="subscribeError"
            class="flex items-start gap-3 rounded-xl bg-red-50 dark:bg-red-900 dark:bg-opacity-20 px-4 py-3" role="alert">
            <icon :path="mdiAlertCircleOutline" :size="18" w="w-5" h="h-5"
                class="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div class="min-w-0">
                <p class="text-sm font-semibold text-red-800 dark:text-red-200">We couldn't start the subscription</p>
                <p class="mt-0.5 text-sm text-red-700 dark:text-red-300">{{ subscribeError }}</p>
            </div>
        </div>

        <!-- ─── Loading skeleton ─── -->
        <div v-if="loading" class="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <div v-for="n in 4" :key="`plan-skeleton-${n}`"
                class="rounded-xl bg-white dark:bg-gray-800 shadow-card p-6 space-y-4">
                <div class="h-5 w-24 rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700 mx-auto" />
                <div class="h-10 w-32 rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700 mx-auto" />
                <div class="h-4 w-full rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
                <div class="h-4 w-2/3 rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
                <div class="h-11 w-full rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
            </div>
        </div>

        <!-- ─── Error ─── -->
        <card-component v-else-if="loadError" disable-collapse>
            <div class="flex flex-col gap-3 py-2 sm:flex-row sm:items-start" role="alert">
                <span
                    class="inline-flex items-center justify-center w-11 h-11 flex-shrink-0 rounded-xl bg-red-100 text-red-600 dark:bg-red-900 dark:bg-opacity-40 dark:text-red-400">
                    <icon :path="mdiAlertCircleOutline" :size="20" w="w-5" h="h-5" />
                </span>
                <div class="min-w-0 flex-1">
                    <p class="text-base font-semibold text-gray-900 dark:text-gray-100">We couldn't load the plans</p>
                    <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{{ loadError }}</p>
                    <jb-button class="mt-4" color="info" :icon="mdiRefresh" label="Try again" @click="loadPlans" />
                </div>
            </div>
        </card-component>

        <!-- ─── Empty ─── -->
        <card-component v-else-if="plans.length === 0" disable-collapse>
            <div class="flex flex-col gap-3 py-2 sm:flex-row sm:items-start">
                <span
                    class="inline-flex items-center justify-center w-11 h-11 flex-shrink-0 rounded-xl bg-brand-100 text-brand-600 dark:bg-brand-900 dark:bg-opacity-40 dark:text-brand-400">
                    <icon :path="mdiWalletOutline" :size="20" w="w-5" h="h-5" />
                </span>
                <div class="min-w-0 flex-1">
                    <p class="text-base font-semibold text-gray-900 dark:text-gray-100">No subscription plans available
                    </p>
                    <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
                        Token packs are still available whenever you need them.
                    </p>
                    <!-- /products, not /replenish: the payment step needs a
                         package already picked and would only say none was. -->
                    <jb-button class="mt-4" color="info" label="Buy tokens" to="/products" />
                </div>
            </div>
        </card-component>

        <!-- ─── Plans ─── -->
        <div v-else class="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <div v-for="plan in plans" :key="plan.name"
                class="relative flex flex-col rounded-xl bg-white dark:bg-gray-800 shadow-card transition duration-150"
                :class="isCurrentPlan(plan) ? 'ring-2 ring-brand-500' : 'hover:ring-2 hover:ring-brand-300 dark:hover:ring-brand-700'">

                <!-- One tinted badge: either the plan you are on, or the shorthand label -->
                <div v-if="isCurrentPlan(plan)" class="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span class="badge-brand gap-1 whitespace-nowrap">
                        <icon :path="mdiCheck" :size="12" w="w-3" h="h-3" />
                        Current plan
                    </span>
                </div>
                <div v-else-if="plan.popular" class="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span class="badge-brand whitespace-nowrap">
                        {{ plan.popularLabel }}
                    </span>
                </div>

                <div class="flex flex-col flex-grow p-6">
                    <!-- Plan header -->
                    <div class="text-center">
                        <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {{ plan.name }}
                        </h3>
                        <!-- With a promo running: the list price struck, and what
                             the first payment actually is. -->
                        <template v-if="planPromoPrice(plan) !== null">
                            <p class="mt-2 text-sm text-gray-400 dark:text-gray-500">
                                <s class="notranslate tabular-nums" translate="no"><span class="sr-only">was </span>€{{
                                    money(plan.price) }}/month</s>
                            </p>
                            <p class="flex flex-wrap items-baseline justify-center gap-x-1.5">
                                <span class="text-sm font-medium text-gray-500 dark:text-gray-400">You pay</span>
                                <span
                                    class="notranslate text-4xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-gray-100"
                                    translate="no">€{{ planPromoPrice(plan) }}</span>
                            </p>
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">for the first month</p>
                        </template>
                        <p v-else class="mt-2 flex items-baseline justify-center gap-1">
                            <span
                                class="notranslate text-4xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-gray-100"
                                translate="no">
                                €{{ money(plan.price) }}
                            </span>
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">/month</span>
                        </p>
                    </div>

                    <!-- Features -->
                    <ul class="mt-6 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-6">
                        <li class="flex items-start gap-2.5">
                            <icon :path="mdiStar" :size="16" w="w-4" h="h-4"
                                class="text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                            <div class="min-w-0">
                                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    <span class="notranslate tabular-nums" translate="no">{{ (plan.tokens +
                                        plan.bonusTokens).toFixed(2) }}</span>
                                    tokens per month
                                </p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">
                                    <span class="notranslate tabular-nums" translate="no">{{ plan.tokens }}</span> +
                                    <span class="font-semibold text-green-600 dark:text-green-400">
                                        <span class="notranslate tabular-nums" translate="no">{{
                                            plan.bonusTokens.toFixed(2) }}</span> bonus
                                    </span>
                                </p>
                            </div>
                        </li>
                        <li class="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                            <icon :path="mdiCheck" :size="16" w="w-4" h="h-4"
                                class="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                            <span>Added to your existing balance every month</span>
                        </li>
                        <li class="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                            <icon :path="mdiCheck" :size="16" w="w-4" h="h-4"
                                class="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                            <span>Cancel anytime from the billing portal</span>
                        </li>
                    </ul>

                    <!-- CTA -->
                    <div class="mt-auto pt-6">
                        <jb-button class="w-full py-3" :color="plan.popular ? 'info' : 'white'"
                            :label="pendingPriceId === plan.priceId ? 'Redirecting…' : 'Subscribe'"
                            :aria-label="`Subscribe to ${plan.name}`"
                            :spinner="pendingPriceId === plan.priceId" :disabled="pendingPriceId !== null"
                            @click="subscribe(plan.productId, plan.priceId)" />
                        <p class="mt-2 text-center text-xs text-gray-500 dark:text-gray-400"
                            :role="pendingPriceId === plan.priceId ? 'status' : null" aria-live="polite">
                            <template v-if="pendingPriceId === plan.priceId">
                                Taking you to Stripe — please don't close this tab.
                            </template>
                            <template v-else-if="isCurrentPlan(plan)">
                                You're already subscribed to this plan.
                            </template>
                            <!-- With a promo, the renewal price is not claimed either
                                 way — only the list price, which is what the plan is. -->
                            <template v-else-if="planPromoPrice(plan) !== null">
                                List price <span class="notranslate" translate="no">€{{ money(plan.price) }}</span>
                                a month, billed monthly until you cancel.
                            </template>
                            <template v-else>
                                <span class="notranslate" translate="no">€{{ money(plan.price) }}</span>
                                billed monthly until you cancel.
                            </template>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
